/** Canvas Kingdom — SoundManager (Audio Tier 1)
 *  SFX: Web Audio API でプログラム的に合成（外部ファイル不要）
 *  BGM: Web Audio API で手続き合成ループ再生（AudioBufferSourceNode.loop）
 */

import { Howler } from 'howler';

export type SfxId =
  | 'brush_start'
  | 'paint_drip'
  | 'enclose_burst'
  | 'trail_cut'
  | 'stage_clear'
  | 'stage_fail';

export type BgmId = 'home' | 'gameplay';

// ---------------------------------------------------------------------------
// Web Audio buffer synthesis helpers
// ---------------------------------------------------------------------------

/** サイン波グライド + ADSR エンベロープでバッファを生成 */
function makeSineBuffer(
  ctx: AudioContext,
  startFreq: number,
  endFreq: number,
  duration: number,
  attack: number,
  decayRate: number,
  volume: number,
): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.ceil(sampleRate * duration);
  const buf = ctx.createBuffer(1, length, sampleRate);
  const data = buf.getChannelData(0);

  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    const progress = duration > 0 ? t / duration : 1;
    // グライド: 瞬時周波数を線形補間し位相を積分
    const instFreq = startFreq + (endFreq - startFreq) * progress;
    const phase = 2 * Math.PI * (startFreq + (endFreq - startFreq) * progress * 0.5) * t;
    const raw = Math.sin(phase);

    // attack → 指数減衰
    let env: number;
    if (t < attack) {
      env = t / attack;
    } else {
      const decayT = (t - attack) / Math.max(duration - attack, 0.001);
      env = Math.exp(-decayT * decayRate);
    }

    data[i] = raw * env * volume;
    void instFreq; // 使用済み変数のlint抑制
  }
  return buf;
}

/** ノコギリ波 + チャープ正弦波ミックス（enclose_burst 用、ハーモニクス豊か） */
function makeSawChirpBuffer(
  ctx: AudioContext,
  baseFreq: number,
  chirpEndFreq: number,
  duration: number,
  volume: number,
): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.ceil(sampleRate * duration);
  const buf = ctx.createBuffer(1, length, sampleRate);
  const data = buf.getChannelData(0);

  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    const progress = t / duration;

    // ノコギリ波（基音 baseFreq）
    const sawPhase = (baseFreq * t) % 1;
    const saw = 2 * sawPhase - 1;

    // チャープ正弦波
    const chirpFreq = baseFreq + (chirpEndFreq - baseFreq) * progress;
    const chirp = Math.sin(2 * Math.PI * chirpFreq * t);

    // エンベロープ: 指数減衰
    const env = Math.exp(-progress * 4);
    data[i] = (saw * 0.6 + chirp * 0.4) * env * volume;
  }
  return buf;
}

/** ホワイトノイズ急減衰バッファ（trail_cut 用、ハイパスフィルタは再生時に適用） */
function makeNoiseBuffer(
  ctx: AudioContext,
  duration: number,
  volume: number,
): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.ceil(sampleRate * duration);
  const buf = ctx.createBuffer(1, length, sampleRate);
  const data = buf.getChannelData(0);

  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    const env = Math.exp(-(t / duration) * 8);
    data[i] = (Math.random() * 2 - 1) * env * volume;
  }
  return buf;
}

/** C メジャー和音ファンファーレバッファ（stage_clear 用） */
function makeMajorChordBuffer(
  ctx: AudioContext,
  freqs: number[],
  duration: number,
  volume: number,
): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.ceil(sampleRate * duration);
  const buf = ctx.createBuffer(1, length, sampleRate);
  const data = buf.getChannelData(0);
  const perVoice = volume / freqs.length;

  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    let env: number;
    if (t < 0.05) {
      env = t / 0.05;
    } else {
      env = Math.exp(-((t - 0.05) / Math.max(duration - 0.05, 0.001)) * 2);
    }

    let sample = 0;
    for (const f of freqs) {
      sample += Math.sin(2 * Math.PI * f * t) * perVoice;
    }
    data[i] = sample * env;
  }
  return buf;
}

// ---------------------------------------------------------------------------
// BGM buffer synthesis
// ---------------------------------------------------------------------------

/**
 * `home` BGM: C major ペンタトニック・アルペジオ、BPM 80、8 秒ループ
 *   コード進行: C → G → Am → F（各 2 秒）
 *   旋律: 上声部 sine（琴的高音）+ 下声部 triangle（柔らかいベース）
 *   音量: 0.15
 */
function buildHomeBgmBuffer(ctx: AudioContext): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const duration = 8.0;
  const length = Math.ceil(sampleRate * duration);
  const buf = ctx.createBuffer(2, length, sampleRate);

  // C major pentatonic notes (Hz): C4 D4 E4 G4 A4 C5 D5 E5 G5
  const penta = [261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33, 659.25, 783.99];

  // Arpeggio sequences per chord (index into penta)
  // C: C4 E4 G4 C5  G: G4 D4 B3(≈246) → use G4 D4 G4 D5  Am: A4 C5 E5 A4  F: F(≈349)→E4 C5 A4 E4
  const chords: number[][] = [
    [penta[0], penta[2], penta[3], penta[5]],  // C: C4 E4 G4 C5
    [penta[3], penta[1], penta[3], penta[6]],  // G: G4 D4 G4 D5
    [penta[4], penta[5], penta[7], penta[4]],  // Am: A4 C5 E5 A4
    [penta[2], penta[5], penta[4], penta[2]],  // F(approx): E4 C5 A4 E4
  ];

  // Bass root notes per chord
  const bassRoots = [65.41, 49.0, 55.0, 43.65]; // C2 G1 A1 F1(≈43.65)

  const bpm = 80;
  const beatSec = 60 / bpm; // 0.75s
  const noteDur = beatSec * 0.7; // note ON duration (with gap)

  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);

  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    // Which chord are we in (2 sec each)
    const chordIdx = Math.floor(t / 2) % 4;
    // Which beat within the 2-sec chord (4 beats × 0.5s each at BPM 120 feel, but we use beatSec)
    const tInChord = t % 2;
    const beatInChord = Math.floor(tInChord / (2 / 4)); // 0-3
    const tInBeat = tInChord % (2 / 4);

    // Melody: sine arpeggiated note
    const melFreq = chords[chordIdx][beatInChord];
    const melEnv = tInBeat < noteDur
      ? (tInBeat < 0.01 ? tInBeat / 0.01 : Math.exp(-(tInBeat - 0.01) * 2.5))
      : 0;
    const mel = Math.sin(2 * Math.PI * melFreq * t) * melEnv * 0.10;

    // Octave shimmer: sine at 2x freq, very soft
    const shimmer = Math.sin(2 * Math.PI * melFreq * 2 * t) * melEnv * 0.03;

    // Bass: triangle wave at root, slow attack, held
    const bassFreq = bassRoots[chordIdx];
    const bassPhase = (bassFreq * t) % 1;
    const bassWave = 2 * Math.abs(2 * bassPhase - 1) - 1; // triangle
    const bassEnv = tInBeat < 0.05 ? tInBeat / 0.05 : Math.exp(-(tInBeat - 0.05) * 1.0);
    const bass = bassWave * bassEnv * 0.05;

    // Seamless loop fade: last 0.3s fade out, first 0.3s fade in
    let loopEnv = 1.0;
    if (t < 0.3) loopEnv = t / 0.3;
    else if (t > duration - 0.3) loopEnv = (duration - t) / 0.3;

    const sample = (mel + shimmer + bass) * loopEnv;
    L[i] = sample;
    R[i] = sample;
  }

  return buf;
}

/**
 * `gameplay` BGM: C major 点描アルペジオ、BPM 90、8 秒ループ
 *   コード進行: C → F → G → C（各 2 秒）
 *   旋律: triangle（軽い短音符の散らばり）+ sine ベース
 *   音量: 0.15
 */
function buildGameplayBgmBuffer(ctx: AudioContext): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const duration = 8.0;
  const length = Math.ceil(sampleRate * duration);
  const buf = ctx.createBuffer(2, length, sampleRate);

  // Chord note sets (triangle, punchy short notes)
  const chordNotes: number[][] = [
    [261.63, 329.63, 392.0,  523.25],  // C: C4 E4 G4 C5
    [349.23, 440.0,  523.25, 349.23],  // F: F4 A4 C5 F4
    [392.0,  493.88, 587.33, 392.0 ],  // G: G4 B4 D5 G4
    [523.25, 659.25, 783.99, 523.25],  // C5: C5 E5 G5 C5
  ];

  // Bass walking line per chord
  const bassNotes = [130.81, 87.31, 98.0, 130.81]; // C3 F2 G2 C3

  const bpm = 90;
  const beatSec = 60 / bpm; // ~0.667s
  const noteDur = beatSec * 0.45; // short punchy notes

  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);

  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    const chordIdx = Math.floor(t / 2) % 4;
    const tInChord = t % 2;
    // 4 notes per 2-sec chord, with syncopation offset on beats 1,2 (+0.05s)
    const noteStep = 2 / 4;
    const beatInChord = Math.floor(tInChord / noteStep);
    const tInNote = tInChord % noteStep;

    // Syncopation: every other note starts slightly late
    const syncOffset = beatInChord % 2 === 1 ? 0.05 : 0;
    const tPlaying = tInNote - syncOffset;

    // Triangle melody: short punchy
    const melFreq = chordNotes[chordIdx][beatInChord];
    const melEnv = (tPlaying >= 0 && tPlaying < noteDur)
      ? (tPlaying < 0.01 ? tPlaying / 0.01 : Math.exp(-(tPlaying - 0.01) * 5))
      : 0;
    // Triangle wave
    const melPhase = (melFreq * t) % 1;
    const melWave = 2 * Math.abs(2 * melPhase - 1) - 1;
    const mel = melWave * melEnv * 0.08;

    // Sine accent on beat 0 of each chord
    const accentFreq = chordNotes[chordIdx][0] * 2;
    const accentEnv = tInChord < 0.15 ? Math.exp(-tInChord * 12) : 0;
    const accent = Math.sin(2 * Math.PI * accentFreq * t) * accentEnv * 0.04;

    // Bass: sine, on the beat
    const bassFreq = bassNotes[chordIdx];
    const bassEnv = tInNote < 0.08 ? tInNote / 0.08 : Math.exp(-(tInNote - 0.08) * 3);
    const bass = Math.sin(2 * Math.PI * bassFreq * t) * bassEnv * 0.07;

    // Seamless loop fade
    let loopEnv = 1.0;
    if (t < 0.3) loopEnv = t / 0.3;
    else if (t > duration - 0.3) loopEnv = (duration - t) / 0.3;

    const sample = (mel + accent + bass) * loopEnv;
    L[i] = sample;
    R[i] = sample;
  }

  return buf;
}

// ---------------------------------------------------------------------------
// SoundManager
// ---------------------------------------------------------------------------

/** BGM 再生中トラックの状態 */
interface BgmTrack {
  source: AudioBufferSourceNode;
  gainNode: GainNode;
}

export class SoundManager {
  private ctx: AudioContext | null = null;
  private sfxBuffers: Map<SfxId, AudioBuffer> = new Map();
  private bgmBuffers: Map<BgmId, AudioBuffer> = new Map();
  private currentBgm: BgmId | null = null;
  private currentBgmTrack: BgmTrack | null = null;
  private muted = false;

  constructor() {
    // AudioContext は suspended 状態で生成（autoplay policy 対応）
    // 初回ユーザー操作時に resume() — main.ts の pointerdown ロジックはそのまま維持
    try {
      this.ctx = new AudioContext();
      this._buildBuffers(this.ctx);
    } catch (e) {
      console.warn('[SoundManager] AudioContext 生成失敗:', e);
    }
  }

  private _buildBuffers(ctx: AudioContext): void {
    // BGM バッファを事前合成（ループ用）
    this.bgmBuffers.set('home', buildHomeBgmBuffer(ctx));
    this.bgmBuffers.set('gameplay', buildGameplayBgmBuffer(ctx));

    // brush_start: サイン波 200Hz → 300Hz グライド 80ms、減衰
    this.sfxBuffers.set(
      'brush_start',
      makeSineBuffer(ctx, 200, 300, 0.08, 0.005, 6, 0.5),
    );

    // paint_drip: サイン波 800Hz、60ms、急減衰（滴の点描感）
    this.sfxBuffers.set(
      'paint_drip',
      makeSineBuffer(ctx, 800, 800, 0.06, 0.002, 12, 0.4),
    );

    // enclose_burst: ノコギリ波 100Hz + 300Hz チャープ、200ms
    this.sfxBuffers.set(
      'enclose_burst',
      makeSawChirpBuffer(ctx, 100, 300, 0.2, 0.7),
    );

    // trail_cut: ホワイトノイズ + ハイパス、150ms ザクッ音（切断 SFX）
    this.sfxBuffers.set(
      'trail_cut',
      makeNoiseBuffer(ctx, 0.15, 0.6),
    );

    // stage_clear: C メジャー和音 C5(523Hz)/E5(659Hz)/G5(784Hz) 1.2 秒ファンファーレ
    this.sfxBuffers.set(
      'stage_clear',
      makeMajorChordBuffer(ctx, [523.25, 659.25, 783.99], 1.2, 0.6),
    );

    // stage_fail: サイン波 200Hz → 150Hz グライド 400ms、優しい下降
    this.sfxBuffers.set(
      'stage_fail',
      makeSineBuffer(ctx, 200, 150, 0.4, 0.01, 3, 0.5),
    );
  }

  /** AudioContext を resume する。main.ts の pointerdown ハンドラから呼ぶ想定。 */
  resumeContext(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch((e) =>
        console.warn('[SoundManager] resume failed:', e),
      );
    }
  }

  playSfx(id: SfxId): void {
    if (this.muted) return;
    const ctx = this.ctx;
    if (!ctx) return;

    // autoplay policy: suspended なら resume してから再生
    if (ctx.state === 'suspended') {
      ctx
        .resume()
        .then(() => this._playSfxNow(id))
        .catch(() => {});
    } else {
      this._playSfxNow(id);
    }
  }

  private _playSfxNow(id: SfxId): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const buf = this.sfxBuffers.get(id);
    if (!buf) return;

    const src = ctx.createBufferSource();
    src.buffer = buf;

    // trail_cut: ハイパスフィルタを挟んでザクッ感を強調
    if (id === 'trail_cut') {
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 2000;
      src.connect(filter);
      filter.connect(ctx.destination);
    } else {
      src.connect(ctx.destination);
    }

    src.start();
    // AudioBufferSourceNode は使い捨て（同時再生 OK）
  }

  // -------------------------------------------------------------------------
  // BGM: Web Audio API で手続き合成ループ再生
  // -------------------------------------------------------------------------

  playBgm(id: BgmId): void {
    if (this.currentBgm === id) return;
    if (this.muted) return;

    const ctx = this.ctx;
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().then(() => this._startBgm(id)).catch(() => {});
    } else {
      this._startBgm(id);
    }
  }

  private _startBgm(id: BgmId): void {
    const ctx = this.ctx;
    if (!ctx) return;

    const buf = this.bgmBuffers.get(id);
    if (!buf) return;

    const fadeOutMs = 200;
    const fadeInMs = 300;

    // Fade out existing track if playing
    if (this.currentBgmTrack) {
      const oldGain = this.currentBgmTrack.gainNode;
      const oldSrc = this.currentBgmTrack.source;
      const now = ctx.currentTime;
      oldGain.gain.setValueAtTime(oldGain.gain.value, now);
      oldGain.gain.linearRampToValueAtTime(0, now + fadeOutMs / 1000);
      setTimeout(() => {
        try { oldSrc.stop(); } catch (_) { /* already stopped */ }
      }, fadeOutMs + 10);
      this.currentBgmTrack = null;
    }

    // Fade in new track
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.15, ctx.currentTime + fadeInMs / 1000);
    gainNode.connect(ctx.destination);

    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.loop = true;
    source.connect(gainNode);
    source.start();

    this.currentBgm = id;
    this.currentBgmTrack = { source, gainNode };
  }

  stopBgm(): void {
    const ctx = this.ctx;
    if (!ctx || !this.currentBgmTrack) {
      this.currentBgm = null;
      return;
    }

    const fadeOutMs = 200;
    const { gainNode, source } = this.currentBgmTrack;
    const now = ctx.currentTime;
    gainNode.gain.setValueAtTime(gainNode.gain.value, now);
    gainNode.gain.linearRampToValueAtTime(0, now + fadeOutMs / 1000);
    setTimeout(() => {
      try { source.stop(); } catch (_) { /* already stopped */ }
    }, fadeOutMs + 10);

    this.currentBgm = null;
    this.currentBgmTrack = null;
  }

  /** Returns true if BGM is currently playing (any track). */
  isBgmPlaying(): boolean {
    return this.currentBgm !== null;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    // Howler の global mute も連動
    Howler.mute(muted);
    // BGM も即停止（ミュート時）
    if (muted && this.currentBgmTrack) {
      const ctx = this.ctx;
      if (ctx) {
        const { gainNode, source } = this.currentBgmTrack;
        const now = ctx.currentTime;
        gainNode.gain.setValueAtTime(gainNode.gain.value, now);
        gainNode.gain.linearRampToValueAtTime(0, now + 0.1);
        setTimeout(() => {
          try { source.stop(); } catch (_) { /* already stopped */ }
        }, 120);
      }
      this.currentBgmTrack = null;
      // currentBgm は保持（unmute 後に再開できるよう）
    }
  }

  isMuted(): boolean {
    return this.muted;
  }
}

/** Global singleton — initialised in main.ts bootstrap, accessible from any module. */
declare global {
  interface Window {
    __ck_sfx?: SoundManager;
  }
}
